import * as React from 'react';
import { useEmbeddedTabs, UseEmbeddedTabsProps, UseEmbeddedTabsReturn } from '../../hooks/useEmbeddedTabs';

/**
 * Context for sharing tab state between EmbeddedTab components
 */
interface EmbeddedTabsContextValue extends UseEmbeddedTabsReturn {}

const EmbeddedTabsContext = React.createContext<EmbeddedTabsContextValue | null>(null);

/**
 * Hook to access EmbeddedTabs context
 * @throws Error if used outside EmbeddedTabs provider
 */
function useEmbeddedTabsContext() {
  const context = React.useContext(EmbeddedTabsContext);
  if (!context) {
    throw new Error('EmbeddedTab components must be used within EmbeddedTabs');
  }
  return context;
}

/**
 * EmbeddedTabs Component
 *
 * Root wrapper component that manages tab state and provides context to child components.
 * Uses the useEmbeddedTabs hook internally and exposes state via React Context.
 *
 * @example
 * <EmbeddedTabs defaultIndex={0} onTabChange={(index) => console.log(index)}>
 *   <EmbeddedTabList className="job-subtabs__nav">
 *     <EmbeddedTab index={0}>Tab 1</EmbeddedTab>
 *     <EmbeddedTab index={1}>Tab 2</EmbeddedTab>
 *   </EmbeddedTabList>
 *   <EmbeddedTabPanel index={0}>Content 1</EmbeddedTabPanel>
 *   <EmbeddedTabPanel index={1}>Content 2</EmbeddedTabPanel>
 * </EmbeddedTabs>
 */
interface EmbeddedTabsProps extends UseEmbeddedTabsProps {
  children: React.ReactNode;
  className?: string;
}

export function EmbeddedTabs({
  children,
  className,
  defaultIndex,
  selectedIndex,
  onTabChange,
}: EmbeddedTabsProps) {
  const tabsApi = useEmbeddedTabs({ defaultIndex, selectedIndex, onTabChange });

  return (
    <EmbeddedTabsContext.Provider value={tabsApi}>
      <div className={className}>{children}</div>
    </EmbeddedTabsContext.Provider>
  );
}

/**
 * EmbeddedTabList Component
 *
 * Renders the tab navigation list with proper ARIA attributes.
 * Must be used within EmbeddedTabs component.
 *
 * @example
 * <EmbeddedTabList className="job-subtabs__nav">
 *   <EmbeddedTab index={0}>Tab 1</EmbeddedTab>
 *   <EmbeddedTab index={1}>Tab 2</EmbeddedTab>
 * </EmbeddedTabList>
 */
interface EmbeddedTabListProps {
  children: React.ReactNode;
  className?: string;
}

export function EmbeddedTabList({ children, className }: EmbeddedTabListProps) {
  const { getTabListProps } = useEmbeddedTabsContext();
  const tabListProps = getTabListProps();

  return (
    <ul className={className} {...tabListProps}>
      {children}
    </ul>
  );
}

/**
 * EmbeddedTab Component
 *
 * Renders an individual tab button with proper structure matching ERB template:
 * <li class="job-subtabs__tab"><button>...</button></li>
 *
 * Automatically registers itself with the tabs system and handles all accessibility.
 * Must be used within EmbeddedTabList component.
 *
 * @example
 * <EmbeddedTab index={0} className="job-subtabs__tab">
 *   <i className="fas fa-users"></i>
 *   <span>My Tab</span>
 *   <span className="badge-modern">5</span>
 * </EmbeddedTab>
 */
interface EmbeddedTabProps {
  index: number;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function EmbeddedTab({ index, children, className, disabled = false }: EmbeddedTabProps) {
  const { getTabProps, registerTab, activeTab } = useEmbeddedTabsContext();

  // Register this tab on mount
  React.useEffect(() => {
    registerTab(index);
  }, [index, registerTab]);

  const tabProps = getTabProps(index);
  const isActive = activeTab === index;

  return (
    <li className={className}>
      <button
        type="button"
        {...tabProps}
        disabled={disabled}
        className={isActive ? 'active' : ''}
        id={`tab-${index}`}
      >
        {children}
      </button>
    </li>
  );
}

/**
 * EmbeddedTabPanel Component
 *
 * Renders a tab content panel with proper ARIA attributes and visibility control.
 * Must be used within EmbeddedTabs component.
 *
 * @example
 * <EmbeddedTabPanel index={0} className="job-subtabs__panel">
 *   <div>Tab content here</div>
 * </EmbeddedTabPanel>
 */
interface EmbeddedTabPanelProps {
  index: number;
  children: React.ReactNode;
  className?: string;
}

export function EmbeddedTabPanel({ index, children, className }: EmbeddedTabPanelProps) {
  const { getTabPanelProps, activeTab } = useEmbeddedTabsContext();
  const panelProps = getTabPanelProps(index);
  const isActive = activeTab === index;

  return (
    <div
      className={`${className || ''} ${isActive ? 'active' : ''}`.trim()}
      {...panelProps}
    >
      {children}
    </div>
  );
}

// Export all components as named exports
export { EmbeddedTabsContext, useEmbeddedTabsContext };
